import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { LinkProps } from "@insite/mobius/Link";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

interface CardModel {
    fields: {
        title: string;
        image: string;
        multilineTextFieldValue: string;
        dateField: string;
    };
}

const enum fields {
    cards = "cards",
    numberOfColumns = "numberOfColumns",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.cards]: CardModel[];
        numberOfColumns: number;
    };
    extendedStyles?: ImageTextCardStyles;
}

export interface ImageTextCardStyles {
    wrapper?: InjectableCss;
    blockWrapper?: InjectableCss;
    blockContent?: InjectableCss;
    blockImage?: InjectableCss;
    image?: LazyImageProps;
    description?: TypographyProps;
    blockTitle?: InjectableCss;
}

export const imageTextCardStyles: ImageTextCardStyles = {
    wrapper: {
        css: css`
            overflow: hidden;
            text-align: center;
            display: flex;
            flex-wrap: wrap;
            margin-bottom: 70px;
        `,
    },
    blockWrapper: {
        css: css`
            display: inline-block;
            text-align: left;
            padding: 0px 10px;
            min-height: 195px;
            vertical-align: top;
            box-sizing: border-box;
        `,
    },
    blockContent: {
        css: css`
            overflow: hidden;
        `,
    },
    blockImage: {
        css: css`
            float: left;
            margin: 10px 20px 10px 10px;
            width: 120px;
            height: 200px;
            padding-right: 10px;
            border-right: 2px solid whitesmoke;
            position: relative;
        `,
    },
    image: {
        css: css`
            position: absolute;
            top: 0;
            bottom: 0;
            right: 10px;
            margin: auto;
            max-width: 120px;
        `,
    },
    description: {
        css: css`
            font-size: 18px;
            line-height: 30px;
            margin: 15px;
        `,
    },
    blockTitle: {
        css: css`
            text-align: left;
            &.emptyDescription {
                margin: 82px 10px 10px 10px;
            }
        `,
    },
};

type Props = OwnProps;

const ImageTextCard: React.FC<Props> = ({ fields, extendedStyles }) => {
    const [styles] = useState(() => mergeToNew(imageTextCardStyles, extendedStyles));
    const desktop = 5;
    const tablet = Math.ceil(desktop / 2);
    const mobile = Math.ceil(tablet / 2);
    const columnCount = 100 / fields.numberOfColumns;
    return (
        <StyledWrapper {...styles.wrapper}>
            {fields.cards &&
                fields.cards.map((card, index) => (
                    <StyledWrapper
                        {...styles.blockWrapper}
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        css={css`
                            width: ${columnCount}%;
                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(theme, [
                                    css`
                                        width: 100%;
                                    `,
                                    css`
                                        width: 100%;
                                    `,
                                    css`
                                        width: 100%;
                                    `,
                                    null,
                                    null,
                                ])}
                        `}
                    >
                        <StyledWrapper {...styles.blockContent}>
                            {card.fields.image && (
                                <StyledWrapper {...styles.blockImage}>
                                    <LazyImage src={card.fields.image} {...styles.image} />
                                </StyledWrapper>
                            )}
                            <StyledWrapper
                                {...styles.blockTitle}
                                className={card.fields.multilineTextFieldValue === "" ? "emptyDescription" : ""}
                            >
                                <Typography as="h3" variant="h3" {...styles.description}>
                                    {card.fields.title}
                                </Typography>
                                <Typography as="p" {...styles.description}>
                                    {card.fields.multilineTextFieldValue}
                                </Typography>
                                <Typography as="h3" variant="h3" {...styles.description}>
                                    {card.fields.dateField}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: ImageTextCard,
    definition: {
        group: "Common",
        icon: "Carousel",
        fieldDefinitions: [
            {
                name: fields.cards,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "multilineTextFieldValue",
                        displayName: "MultilineText",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                    },
                    {
                        name: "dateField",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: "numberOfColumns",
                editorTemplate: "DropDownField",
                defaultValue: 3,
                options: [{ value: 2 }, { value: 3 }],
                isRequired: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
